import { useCallback, useEffect, useState } from 'react';
import api from '../api';
import { QUALITY_SERVICE_API_URL } from '../config';

async function fetchMeanTimeToResolveBreakdown(breakdown, queryString) {
  const url = `${QUALITY_SERVICE_API_URL}/hub/mean-time-to-resolve/breakdown?breakdown=${breakdown}${queryString ? `&${queryString}` : ''}`;
  try {
    const response = await api.get(url);
    return response.data;
  } catch (err) {
    console.error('[useMeanTimeToResolveBreakdown] fetch failed:', {
      breakdown,
      status: err.response?.status,
      message: err.message,
    });
    throw err;
  }
}

function useMeanTimeToResolveBreakdown(breakdown, filters, dateRange) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Build query string
  const params = new URLSearchParams();
  ['domain', 'group', 'unit'].forEach(key => {
    (filters?.[key] || []).forEach(value => params.append(key, value));
  });

  const formatDate = date => `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
  if (dateRange?.startDate) params.set('from', formatDate(dateRange.startDate));
  if (dateRange?.endDate) params.set('to', formatDate(dateRange.endDate));

  const queryString = params.toString();

  const fetchBreakdown = useCallback(async (isCancelled = () => false) => {
    if (!dateRange?.startDate || !dateRange?.endDate) {
      setData(null);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    setData(null);
    try {
      const result = await fetchMeanTimeToResolveBreakdown(breakdown, queryString);
      if (!isCancelled()) setData(result);
    } catch (err) {
      if (!isCancelled()) setError(err);
    } finally {
      if (!isCancelled()) setLoading(false);
    }
  }, [breakdown, queryString, dateRange?.startDate, dateRange?.endDate]);

  useEffect(() => {
    let cancelled = false;
    fetchBreakdown(() => cancelled);
    return () => { cancelled = true; };
  }, [fetchBreakdown]);

  return { data, loading, error, refetch: () => fetchBreakdown() };
}

export default useMeanTimeToResolveBreakdown;
